// src/components/AssistenteGuida.js
// «Come posso aiutarti?» (ottobre 2026) — in cima alla Guida dell'app. Gratuito e senza
// intelligenza artificiale: cerca la domanda nelle schede del Centro assistenza
// (utils/assistenteGuida.js) e propone le schede giuste con il passaggio più vicino.
// Se non trova una risposta sicura lo dice e propone di scrivere all'assistenza, con la
// domanda già scritta. Le domande non si salvano da nessuna parte.
import React, { useEffect, useMemo, useState } from "react";
import { Search, ExternalLink, Mail } from "lucide-react";
import { cercaNellaGuida, testoRisposta, rispostaFissa } from "../utils/assistenteGuida";
import { urlScheda, paginaDiScheda, EMAIL_ASSISTENZA } from "../utils/guidaOnline";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";

const ETICHETTA = { passo: n => `Passo ${n}`, problema: () => "Problema frequente", campo: () => "Cosa inserire", serve: () => "A cosa serve", quando: () => "Quando usarlo", dove: () => "Dove si trova" };
const ESEMPI = ["Come faccio firmare il rapportino al cliente?", "Il margine è vuoto", "Come invito un collega?"];

// compatto = dentro la finestra di Nexi (components/Nexi.js): senza cornice né titolo.
// esempi = domande pronte adatte alla pagina in cui ci si trova.
export default function AssistenteGuida({ puoAprire, onApri, compatto = false, esempi = ESEMPI }) {
  const { currentUser } = useAuth();
  const { orgVisitata } = useOrg();
  const [domanda, setDomanda] = useState("");
  const [cercata, setCercata] = useState("");
  const [indice, setIndice] = useState(null);

  // I testi della guida (circa 130 KB) si caricano solo quando si apre la Guida
  useEffect(() => {
    let vivo = true;
    import("../utils/indiceGuidaDati").then(m => { if (vivo) setIndice(m.default); }).catch(() => { if (vivo) setIndice([]); });
    return () => { vivo = false; };
  }, []);

  // Si cerca mentre si scrive, con un attimo di pausa
  useEffect(() => { const t = setTimeout(() => setCercata(domanda.trim()), 250); return () => clearTimeout(t); }, [domanda]);

  const fissa = useMemo(() => cercata.length >= 3 ? rispostaFissa(cercata) : null, [cercata]);
  // Con una risposta fissa (password, prezzi, prova, telefono) non si aggiungono schede trovate per caso
  const risultati = useMemo(() => (indice && cercata.length >= 3 && !fissa ? cercaNellaGuida(indice, cercata) : []), [indice, cercata, fissa]);
  const senzaRisposta = cercata.length >= 3 && indice && !fissa && risultati.length === 0;

  const mailto = () => {
    const corpo = `Domanda: ${cercata}\n\nAzienda: ${orgVisitata?.nome || orgVisitata?.name || ""}\nUtente: ${currentUser?.displayName || ""} (${currentUser?.email || ""})\n`;
    return `mailto:${EMAIL_ASSISTENZA}?subject=${encodeURIComponent("Assistenza Nexiora: " + cercata.slice(0, 60))}&body=${encodeURIComponent(corpo)}`;
  };

  const btn = { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 700, borderRadius: 7, padding: "5px 10px", cursor: "pointer", textDecoration: "none", fontFamily: "'Inter',sans-serif" };

  return (
    <div style={compatto ? {} : { background: "linear-gradient(180deg,#faf5ff,#fff)", border: "1px solid #e9d5ff", borderRadius: 12, padding: "14px 16px", marginBottom: 20 }}>
      {!compatto && <div style={{ fontSize: 14, fontWeight: 800, color: "#4c1d95", marginBottom: 8 }}>💬 Come posso aiutarti?</div>}
      <div style={{ position: "relative" }}>
        <Search size={15} color="#7c3aed" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
        <input value={domanda} onChange={e => setDomanda(e.target.value)} autoFocus
          placeholder={compatto ? "Scrivi cosa vuoi fare…" : "Scrivi la tua domanda, es. come faccio firmare il rapportino al cliente?"}
          aria-label="Scrivi la tua domanda"
          style={{ width: "100%", boxSizing: "border-box", padding: "11px 12px 11px 36px", border: "1.5px solid #ddd6fe", borderRadius: 10, fontSize: 13.5, fontFamily: "'Inter',sans-serif", outline: "none", background: "#fff" }} />
      </div>

      {cercata.length < 3 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {esempi.map(e => <button key={e} onClick={() => setDomanda(e)} style={{ ...btn, fontWeight: 600, color: "#6d28d9", background: "#fff", border: "1px solid #e9d5ff" }}>{e}</button>)}
        </div>
      )}

      {cercata.length >= 3 && !indice && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 10 }}>Cerco nella guida…</div>}

      {fissa && (
        <div style={{ background: "#fff", border: "1px solid #ede9fe", borderRadius: 10, padding: "10px 12px", marginTop: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{fissa.titolo}</div>
          <div style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.55, marginTop: 3 }}>{fissa.x}</div>
          <a href={fissa.link} target="_blank" rel="noopener noreferrer" style={{ ...btn, marginTop: 8, color: "#6d28d9", background: "#faf5ff", border: "1px solid #e9d5ff" }}>Apri <ExternalLink size={11} /></a>
        </div>
      )}

      {risultati.map((r, i) => {
        const pagina = paginaDiScheda(r.id);
        const apribile = pagina && puoAprire && puoAprire(pagina);
        const etichetta = r.risposta ? ETICHETTA[r.risposta.tipo]?.(r.risposta.n) : null;
        const testo = r.risposta ? (r.risposta.tipo === "problema" ? `${r.risposta.q} — ${testoRisposta(r.risposta)}` : testoRisposta(r.risposta)) : testoRisposta({ x: r.sintesi });
        return (
          <div key={r.id} style={{ background: "#fff", border: `1px solid ${i === 0 ? "#c4b5fd" : "#ede9fe"}`, borderRadius: 10, padding: "10px 12px", marginTop: 10 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{r.titolo}</span>
              <span style={{ fontSize: 10.5, color: "#9ca3af" }}>{r.area}</span>
            </div>
            {etichetta && <div style={{ fontSize: 10.5, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: ".04em", marginTop: 4 }}>{etichetta}</div>}
            <div style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.55, marginTop: 2 }}>{testo}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
              {/* la scheda si apre dal suo inizio (titolo, dove si trova, a cosa serve), non a metà */}
              <a href={urlScheda(r.id)} target="_blank" rel="noopener noreferrer"
                title="La scheda completa sul sito, con le schermate (si apre in una nuova scheda)"
                style={{ ...btn, color: "#fff", background: "#7c3aed", border: "1px solid #7c3aed" }}>Guida passo passo <ExternalLink size={11} /></a>
              {apribile && <button onClick={() => onApri(pagina)} style={{ ...btn, color: "#6d28d9", background: "#faf5ff", border: "1px solid #e9d5ff" }}>Vai alla sezione</button>}
            </div>
          </div>
        );
      })}

      {senzaRisposta && (
        <div style={{ background: "#fff", border: "1px dashed #c4b5fd", borderRadius: 10, padding: "10px 12px", marginTop: 10, fontSize: 12.5, color: "#374151", lineHeight: 1.55 }}>
          Non ho trovato una risposta sicura nella guida. Prova con altre parole, oppure scrivi all'assistenza: ti rispondiamo noi.
          <div style={{ marginTop: 8 }}><a href={mailto()} style={{ ...btn, color: "#fff", background: "#7c3aed", border: "1px solid #7c3aed" }}><Mail size={12} /> Scrivi a {EMAIL_ASSISTENZA}</a></div>
        </div>
      )}
      {(risultati.length > 0 || fissa) && (
        <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 10 }}>
          Non è quello che cercavi? <a href={mailto()} style={{ color: "#6d28d9", fontWeight: 700 }}>Scrivi all'assistenza</a>
        </div>
      )}
    </div>
  );
}
